import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight, MapPin, Users, Leaf, Quote } from "lucide-react";
import { farms } from "@/data/farms";
import { Reveal } from "@/components/ui/reveal";
import TypedHandStyle from "@/components/ui/TypedHandStyle";

export default function AboutPage() {
  const roskamFarm = farms.find((farm) => farm.id === "boerderij-roskam") || farms[0];
  const storyParagraphs = roskamFarm.storyFull.split("\n\n");
  const quoteCardRef = useRef(null);
  const [startQuoteTyping, setStartQuoteTyping] = useState(false);

  useEffect(() => {
    const card = quoteCardRef.current;
    if (!card || startQuoteTyping) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStartQuoteTyping(true);
        observer.disconnect();
      },
      { threshold: 0.4 }
    );

    observer.observe(card);

    return () => observer.disconnect();
  }, [startQuoteTyping]);

  return (
    <div>
      {/* Intro Banner */}
      <section className="py-12 lg:py-16 bg-secondary/20 border-b border-border">
        <Reveal as="div" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" variant="up">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">About</span>
          </nav>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Boerderij Roskam</p>
              <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground mt-3">
                The Roskam Farm Story
              </h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
                A family-run sheep farm on the border of Noordoostpolder and Overijssel.
              </p>
            </div>
            <Button asChild size="lg" className="btn-premium">
              <a href="/#waitlist">Join the waitlist</a>
            </Button>
          </div>
        </Reveal>
      </section>

      {/* Hero Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <Reveal as="div" className="lg:col-span-7" variant="left">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden tilt-card">
                <img
                  src={roskamFarm.heroImage}
                  alt={roskamFarm.name}
                  className="w-full h-full object-cover object-[center_35%] transition-transform ease-out hover:scale-[1.08]"
                  style={{ transitionDuration: "1.8s" }}
                />
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Card className="border-border/60 bg-secondary/20 tilt-card">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground">Family</h3>
                    <p className="text-sm text-muted-foreground mt-2">{roskamFarm.farmerName}</p>
                  </CardContent>
                </Card>
                <Card className="border-border/60 bg-secondary/20 tilt-card">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Leaf className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground">Flock</h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      {roskamFarm.flockSize} · {roskamFarm.sheepBreed}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </Reveal>

            <Reveal as="div" className="lg:col-span-5 space-y-6" delay={100} variant="right">
              <Card ref={quoteCardRef} className="border-border/60">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Quote className="h-5 w-5 text-primary mt-1" />
                    <TypedHandStyle
                      text={`“${roskamFarm.farmerQuote}”`}
                      start={startQuoteTyping}
                      startDelayMs={120}
                      speedMultiplier={1.28}
                      className="text-muted-foreground leading-relaxed"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border/60 tilt-card">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-foreground">Location</h3>
                  <p className="text-sm text-muted-foreground mt-2">{roskamFarm.location}</p>
                </CardContent>
              </Card>
              <div className="rounded-2xl overflow-hidden tilt-card">
                <img
                  src={roskamFarm.images[1]}
                  alt={`${roskamFarm.name} detail`}
                  className="w-full h-full object-cover transition-transform ease-out hover:scale-[1.08]"
                  style={{ transitionDuration: "1.8s" }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-24 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <Reveal as="div" className="lg:col-span-4" variant="left">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">History</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mt-3">
                A farm shaped by family
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Decades of sheep farming, local land stewardship, and careful growth.
              </p>
            </Reveal>
            <Reveal as="div" className="lg:col-span-8 space-y-6 text-muted-foreground leading-relaxed" delay={120} variant="right">
              {storyParagraphs.map((paragraph, index) => (
                <p key={index} className="stagger-item" style={{ "--stagger-delay": `${index * 90}ms` }}>
                  {paragraph}
                </p>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal as="div" className="flex items-end justify-between gap-6 mb-10">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">Farm Gallery</h2>
              <p className="mt-3 text-lg text-muted-foreground">
                A view into the daily rhythm of Boerderij Roskam.
              </p>
            </div>
            <Button asChild variant="outline" className="hidden sm:inline-flex">
              <a href="/#waitlist">Join the waitlist</a>
            </Button>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {roskamFarm.images.map((image, index) => (
              <Reveal
                key={index}
                as="div"
                delay={index * 80}
                variant={index % 2 === 0 ? "up" : "pop"}
                className="aspect-[4/3] rounded-2xl overflow-hidden tilt-card"
              >
                <img
                  src={image}
                  alt={`${roskamFarm.name} ${index + 1}`}
                  className="w-full h-full object-cover transition-transform ease-out hover:scale-[1.08]"
                  style={{ transitionDuration: "1.8s" }}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
